/**
 * ACTIVE SONG — what the workspace is currently working on, and nothing else.
 *
 *   ACTIVE SONG
 *   ┌─────────────┐  Golden Hour
 *   │             │  Lo-Fi instrumental
 *   │   COVER      │  4:05 · 74 BPM · Nostalgic · English
 *   │   ARTWORK    │
 *   │             │  ♥ Favorite   Share   Download   ⋯
 *   └─────────────┘
 *
 * This panel identifies the track. It is NOT a player: no play/pause, no scrubber,
 * no waveform, no volume, no position readout. Every one of those lives in the
 * TransportBar at the bottom of the workspace, which is the single place playback
 * is controlled from.
 *
 * That separation is the design. Two play buttons on one screen means two things
 * that can disagree — and even when they cannot, the user has to work out which one
 * is authoritative. One transport, always visible, is unambiguous.
 *
 * The four actions here all act on the *song as a thing you own* rather than on its
 * playback: mark it, send it, save it, or manage it. Extend/Remix/Replace and the
 * rest are creative tools and stay in the AI TOOLS rail, where their credit costs
 * are shown.
 *
 * The metadata line shows only fields the song actually carries. There is no
 * musical key in the domain model — no generator ever reported one — so no key is
 * displayed. Inventing "C Major" would put fabricated data on the page, which is
 * worse than an absent field.
 *
 * The artwork is a button: clicking a song's cover to work on its cover is the
 * obvious gesture, and it saves the trip through the rail.
 *
 * SIMULATED. The cover is a deterministic gradient rather than a generated image
 * (see CoverArt), and Download writes a details text file because there is no audio
 * (see useSongs.downloadSong).
 */
import {
  Download,
  History,
  Image as ImageIcon,
  ListPlus,
  Pencil,
  Share2,
  Star,
  Trash2,
} from 'lucide-react';
import { ActionMenu, Btn } from '../ui';
import CoverArt from './CoverArt';
import { hasCoverArt } from '../../lib/artwork';
import { joinWithMiddleDot } from '../../lib/formatting';
import { c } from '../../styles/theme';
import type { Song, StudioDrawer } from '../../types/domain';

export interface ActiveSongProps {
  song: Song;
  onToggleFavorite: () => void;
  /** Opens the share dialog. */
  onShare: () => void;
  /** Saves the song, the same action the transport's Download menu item runs. */
  onDownload: () => void;
  onDelete: () => void;
  /** Raises the "Add to playlist" sheet — §12's Studio entry point. */
  onAddToPlaylist: () => void;
  /**
   * Raises the existing Edit Song modal — §14's Studio entry point.
   *
   * The SAME modal the library row opens, on the same uiStore target, writing through the
   * same `updateSong`. Not a second metadata form: the song's fields are defined once in
   * types/domain and edited in one component, and a Studio-specific copy would be a second
   * place for those definitions to drift.
   */
  onEditMetadata: () => void;
  /** Opens a panel in the centre column — the artwork and the menu both do. */
  onSelectTool: (panel: StudioDrawer) => void;
  /** The open panel, so the artwork can mark itself active. */
  drawer: StudioDrawer;
}

/**
 * The metadata line, assembled from fields that are present.
 *
 * Filtered rather than templated so a song missing a value does not render a
 * stray separator — and so adding a field later (a real musical key, once the
 * generator reports one) is one entry in this list.
 */
function metaLine(song: Song): string {
  return joinWithMiddleDot([song.length, `${song.bpm} BPM`, song.mood, song.language]);
}

export default function ActiveSong({
  song,
  onToggleFavorite,
  onShare,
  onDownload,
  onDelete,
  onAddToPlaylist,
  onEditMetadata,
  onSelectTool,
  drawer,
}: ActiveSongProps) {
  const art = hasCoverArt(song);

  return (
    <section
      className="lyx-ws-active"
      aria-label="Active song"
      style={{ borderBottom: `1px solid ${c.border}` }}
    >
      <p className="lyx-ws-eyebrow">Active song</p>

      <div className="lyx-ws-active-body">
        {/* ---- the cover ---- */}
        <button
          type="button"
          onClick={() => onSelectTool('cover')}
          className="lyx-ws-active-cover"
          aria-label={art ? `Cover art for ${song.title} — edit` : 'Add cover art for this song'}
          style={{
            background: c.surface2,
            border: `1px ${art ? 'solid' : 'dashed'} ${
              drawer === 'cover' ? c.accentBorder : c.border
            }`,
          }}
        >
          {art ? (
            <CoverArt song={song} depth="hero" radius={11} />
          ) : (
            <span className="lyx-ws-active-cover-empty">
              <ImageIcon size={22} color={c.text2} aria-hidden="true" />
              <span style={{ fontSize: 11, color: c.text2 }}>Add cover art</span>
            </span>
          )}
        </button>

        {/* ---- title, type, metadata, actions ---- */}
        <div className="lyx-ws-active-main">
          <div>
            {/* Typography lives in CSS, not inline: the size steps down on a phone,
                and an inline style prop outranks a media query. */}
            <h2 className="lyx-ws-active-title">{song.title}</h2>
            {/* The genre is the song's type, given its own line at the size the
                reference layout gives it — it is how you recognise the track, not a
                footnote to the title. */}
            {song.genre && <p className="lyx-ws-active-genre">{song.genre}</p>}
            <p className="lyx-ws-active-meta">{metaLine(song)}</p>
          </div>

          <div className="lyx-ws-active-actions lyx-row-controls">
            {/* Favourite leads: it is the only one of the four that changes the
                song, so it is the only one drawn as a primary action. */}
            <Btn onClick={onToggleFavorite}>
              <Star
                size={13}
                color={song.favorite ? '#1A1300' : c.text2}
                fill={song.favorite ? '#1A1300' : 'none'}
              />
              {song.favorite ? 'Favorited' : 'Favorite'}
            </Btn>

            <Btn variant="secondary" onClick={onShare}>
              <Share2 size={13} />
              Share
            </Btn>

            <Btn variant="secondary" onClick={onDownload}>
              <Download size={13} />
              Download
            </Btn>

            {/* Export opens the Download Center — every format in one place — while
                the Download button beside it saves immediately. Both existed before
                this panel did; keeping the quick one visible and the chooser in the
                menu is what stops the row growing to five buttons. */}
            <ActionMenu
              label={`More actions for ${song.title}`}
              // Not the song-scoped name: the bubble is inches from the title.
              tooltip="More actions"
              items={[
                {
                  // First of the five, because it is the only one that changes the facts
                  // this panel is displaying — the title and genre above it are exactly
                  // what it edits. It opens the library's own Edit Song modal; see
                  // ActiveSongProps.onEditMetadata for why it is not a second form.
                  label: 'Edit metadata',
                  icon: <Pencil size={13} color={c.text2} />,
                  onSelect: onEditMetadata,
                },
                {
                  // The same menu entry as the library row's, worded the same, because
                  // it is the same action on the same kind of thing — §12 asks for the
                  // gesture in both places and there is no reason for it to differ.
                  label: 'Add to playlist',
                  icon: <ListPlus size={13} color={c.text2} />,
                  onSelect: onAddToPlaylist,
                },
                {
                  label: 'Export options',
                  icon: <Download size={13} color={c.text2} />,
                  onSelect: () => onSelectTool('downloads'),
                },
                {
                  label: 'Version history',
                  icon: <History size={13} color={c.text2} />,
                  onSelect: () => onSelectTool('versions'),
                },
                {
                  label: 'Delete song',
                  icon: <Trash2 size={13} color={c.danger} />,
                  onSelect: onDelete,
                  danger: true,
                },
              ]}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
